import React, { useMemo } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { DriverDetails } from '../../types/predictions';
import { useChartTokens } from './chartKit';

interface Props {
  driversDetails: Record<string, DriverDetails[]>;
}

// Ordered finish buckets: one green hue from strongest (win) to weakest, plus a neutral gray for DNFs. On the dark
// surface the order runs bright to dim, so "better" always reads as more prominent.
const BUCKETS = [
  { key: 'win', label: 'Win', test: (r: DriverDetails) => !r.DNF && r.Pos === 1 },
  { key: 't5', label: '2–5', test: (r: DriverDetails) => !r.DNF && r.Pos >= 2 && r.Pos <= 5 },
  { key: 't10', label: '6–10', test: (r: DriverDetails) => !r.DNF && r.Pos >= 6 && r.Pos <= 10 },
  { key: 't20', label: '11–20', test: (r: DriverDetails) => !r.DNF && r.Pos >= 11 && r.Pos <= 20 },
  { key: 'rest', label: '21+', test: (r: DriverDetails) => !r.DNF && r.Pos >= 21 },
  { key: 'dnf', label: 'DNF', test: (r: DriverDetails) => !!r.DNF },
];
const GREENS_LIGHT = ['#1b5e20', '#2e7d32', '#66bb6a', '#a5d6a7', '#dcedc8'];
const GREENS_DARK = ['#c5e1a5', '#81c784', '#4caf50', '#2e7d32', '#1f4d25'];
const DNF_LIGHT = '#9e9e9e';
const DNF_DARK = '#6b6b6b';

/** How each driver's finishes spread across win / top 5 / top 10 / mid-pack / back / DNF (share of races). */
export const FinishSpreadChart: React.FC<Props> = ({ driversDetails }) => {
  const tokens = useChartTokens();
  const colors = [...(tokens.dark ? GREENS_DARK : GREENS_LIGHT), tokens.dark ? DNF_DARK : DNF_LIGHT];
  // Text on a segment: dark ink on light fills, light ink on dark fills.
  const ink = (i: number) => {
    const lightFill = tokens.dark ? i <= 2 : i >= 2 && i !== 5;
    return lightFill ? '#0b0b0b' : '#ffffff';
  };

  const rows = useMemo(
    () =>
      Object.entries(driversDetails).map(([driver, races]) => {
        const counts = BUCKETS.map((b) => races.filter(b.test).length);
        return { driver, total: races.length, counts };
      }),
    [driversDetails],
  );

  return (
    <Box>
      {/* Legend */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {BUCKETS.map((b, i) => (
          <Stack key={b.key} direction="row" alignItems="center" spacing={0.5}>
            <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: colors[i] }} />
            <Typography variant="body2">{b.label}</Typography>
          </Stack>
        ))}
      </Stack>
      {rows.map((r) => (
        <Box
          key={r.driver}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '150px 1fr' },
            alignItems: 'center',
            gap: 1,
            py: 0.5,
          }}
        >
          <Typography variant="body2" noWrap>
            {r.driver}
            <Typography component="span" variant="caption" color="text.secondary">
              {' '}
              ({r.total})
            </Typography>
          </Typography>
          <Box sx={{ display: 'flex', height: 22, gap: '2px' }}>
            {r.counts.map((c, i) =>
              c === 0 ? null : (
                <Tooltip
                  key={i}
                  title={`${r.driver}: ${BUCKETS[i].label} in ${c} of ${r.total} races (${Math.round((c / r.total) * 100)}%)`}
                  placement="top"
                >
                  <Box
                    sx={{
                      flex: c,
                      bgcolor: colors[i],
                      color: ink(i),
                      fontSize: 11,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      '&:first-of-type': { borderTopLeftRadius: 4, borderBottomLeftRadius: 4 },
                      '&:last-of-type': { borderTopRightRadius: 4, borderBottomRightRadius: 4 },
                    }}
                  >
                    {c / r.total >= 0.1 ? `${Math.round((c / r.total) * 100)}%` : ''}
                  </Box>
                </Tooltip>
              ),
            )}
          </Box>
        </Box>
      ))}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Share of the races behind each driver's rating (race count in brackets). A wide green start with little gray is
        a consistent driver; big ends at both sides is boom-or-bust.
      </Typography>
    </Box>
  );
};
